// src/connectors/components/RegisterConnectionModal.jsx
//
// Registration form for one connector family. Provider select drives which
// fields render (see constants.js's per-provider field schema) — e.g. Azure
// OpenAI needs endpoint/API key/deployment, Ollama needs just a base URL.
// Per requirements FR-C3: runs the tester immediately after saving and
// shows a clear three-state result (untested/passed/failed) rather than
// silently closing, so the user always knows whether what they just
// registered actually works.

import { useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, XCircle } from "lucide-react";
import { Modal, FormField, Input, PasswordInput, Select, Button, Alert, useDiscardConfirm } from "../../shared/ui";
import { PROVIDERS_BY_FAMILY, getProvider } from "../constants";
import ProviderIcon from "./ProviderIcon";

const FAMILY_LABEL = {
  source_control: "source control connection",
  work_item: "work item connection",
  crm: "CRM connection",
};

export default function RegisterConnectionModal({ open, family, onClose, onRegister }) {
  const providers = PROVIDERS_BY_FAMILY[family] || [];
  const [providerValue, setProviderValue] = useState(providers[0]?.value ?? "");
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { status, message } once submitted
  const [dirty, setDirty] = useState(false);

  const provider = getProvider(family, providerValue);

  function reset() {
    setProviderValue(providers[0]?.value ?? "");
    setValues({});
    setErrors({});
    setSubmitting(false);
    setResult(null);
    setDirty(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !result, handleClose);

  function setField(name, v) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!provider) return;

    const nextErrors = {};
    provider.fields.forEach((f) => {
      if (f.required && !String(values[f.name] || "").trim()) {
        nextErrors[f.name] = `${f.label} is required.`;
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const { testResult } = await onRegister({ name: values.name, provider: providerValue, ...values });
      setResult(testResult);
    } catch (err) {
      setResult({ status: "failed", message: err?.message || "Registration failed." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal open={open} title={`Register ${FAMILY_LABEL[family] || "connection"}`} onClose={requestClose}>
        {result ? (
          <div className="flex flex-col gap-4">
            <Alert
              variant={result.status === "passed" ? "success" : "danger"}
              icon={result.status === "passed" ? CheckCircle2 : XCircle}
              title={result.status === "passed" ? "Connection saved and verified" : "Connection saved, but the test failed"}
            >
              {result.message}
            </Alert>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={reset}>Register another</Button>
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            {providers.length > 1 && (
              <FormField label="Provider" required>
                <div className="grid grid-cols-2 gap-2">
                  {providers.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => { setProviderValue(p.value); setValues({}); setErrors({}); setDirty(true); }}
                      className={[
                        "flex items-center gap-2.5 p-3 rounded-xl border-2 text-left transition-colors",
                        p.value === providerValue ? "border-brand-600 bg-brand-50" : "border-line bg-white hover:border-brand-500/50",
                      ].join(" ")}
                    >
                      <ProviderIcon initials={p.initials} size={26} />
                      <span className="text-sm font-semibold text-ink">{p.label}</span>
                    </button>
                  ))}
                </div>
              </FormField>
            )}

            {provider?.fields.map((f) => (
              <FormField
                key={f.name}
                label={f.label}
                required={f.required}
                helperText={f.helperText}
                error={Boolean(errors[f.name])}
                errorText={errors[f.name]}
              >
                {f.type === "password" ? (
                  <PasswordInput
                    value={values[f.name] || ""}
                    onChange={(e) => setField(f.name, e.target.value)}
                    placeholder={f.placeholder}
                  />
                ) : f.type === "select" ? (
                  <Select
                    options={f.options}
                    value={values[f.name] || f.options?.[0]?.value || ""}
                    onChange={(e) => setField(f.name, e.target.value)}
                  />
                ) : (
                  <Input
                    value={values[f.name] || ""}
                    onChange={(e) => setField(f.name, e.target.value)}
                    placeholder={f.placeholder}
                  />
                )}
              </FormField>
            ))}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button type="submit" variant="primary" loading={submitting}>
                Register &amp; test
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

RegisterConnectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  family: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onRegister: PropTypes.func.isRequired,
};
